import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ProjectCard from "./ProjectCard";
import TitleWithContent from "@/components/elements/TitleWithContent/TitleWithContent";
import { motion } from "framer-motion";
import { usePageLoading } from "@/hooks/usePageLoading";
import ButtonElement from "@/components/elements/Button/Button";
import { client } from "@/lib/sanity";

interface Project {
  _id?: string;
  id?: string;
  title: string;
  category: string;
  slug?: string | { current: string };
  imageUrl: string;
  _updatedAt: string;
}

const MAIN_CATEGORIES = ["Ongoing Projects", "Completed Projects"];

const ProjectListPage = () => {
  const { isLoading } = usePageLoading();
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize selectedCategory based on URL pathname
  // so UI reflects URL even on page reload
  const initialCategory =
    location.pathname === "/projects/completed-subcategories"
      ? "Completed Projects"
      : "Ongoing Projects";

  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

 useEffect(() => {
  const fetchProjects = async () => {
    try {
      const sanityProjects = await client.fetch(`*[_type == "project"]{
        _id,
        title,
        category,
        "slug": slug.current,
        "imageUrl": image.asset->url,
        _updatedAt
      }`);

      const localProjectsRes = await fetch("/projects.json");
      const localProjects = await localProjectsRes.json();

      // Assign some default _updatedAt to local projects if missing
      const localWithDates = localProjects.map(p => ({
        ...p,
        _updatedAt: p._updatedAt || new Date().toISOString(), // or some fallback
      }));

      // Combine projects
      const combined = [...sanityProjects, ...localWithDates];

      setProjects(combined);
    } catch (err) {
      console.error("Error loading projects", err);
    } finally {
      setLoading(false);
    }
  };
  fetchProjects();
}, []);

  // When category button clicked, update state AND navigate
  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
    if (category === "Completed Projects") {
      navigate("/projects/completed-subcategories");
    } else {
      navigate("/projects");
    }
  };

  // Filter projects by selected category (only used for ongoing here)
 const filteredProjects = projects
  .filter(p => p.category === selectedCategory)
  .sort((a, b) => new Date(b._updatedAt).getTime() - new Date(a._updatedAt).getTime());
  
  if (loading) return <p className="text-center mt-24 text-gray-500">Loading projects...</p>;

  return (
    <div className="2xl:px-40 xl:px-35 lg:px-20 md:px-15 px-3 my-[5rem] ">
      <motion.div
        initial={{ opacity: 0, scaleX: 0 }}
        animate={!isLoading ? { opacity: 1, scaleX: 1 } : { opacity: 0, scaleX: 0 }}
        transition={{ duration: 0.5 }}
      >
        <TitleWithContent title="Project Categories" />
      </motion.div>

      <div className="flex flex-col md:flex-row gap-10 px-5 md:px-0">
        {/* Sidebar Categories */}
        <div className="relative w-full max-w-[320px] mx-auto 2xl:ml-[7rem] xl:ml-[5rem] lg:ml-[4rem] flex flex-col">
          {MAIN_CATEGORIES.map((category) => {
            const isActive = selectedCategory === category;
            return (
              <ButtonElement
                key={category}
                btnText={category}
                onClick={() => handleCategoryClick(category)}
                className={`w-full rounded-sm !py-3 flex text-left mt-[1px] ${
                  isActive
                    ? "bg-custom-secolor font-semibold border-l-4 border-white bg-opacity-70"
                    : "bg-custom-secolor hover:bg-custom-secolor"
                }`}
              />
            );
          })}
        </div>

        {/* Project Cards for Ongoing Projects */}
        <div className="flex-1 ">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {selectedCategory === "Ongoing Projects" &&
              filteredProjects.map((project) => (
                <ProjectCard
                  key={project._id || project.id || (typeof project.slug === 'string' ? project.slug : project.slug?.current)}
                  id={project.id}
                  slug={typeof project.slug === 'string' ? project.slug : project.slug?.current}
                  imageUrl={project.imageUrl}
                  title={project.title}
                  category={project.category}
                />
              ))}
            {/* If Completed Projects is selected, we don't show projects here,
                since that page has its own subcategory component */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectListPage;
